import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Empty, Select, Label, Textarea } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { useNavigate } from "react-router-dom";
import { Search, Plus, X } from "lucide-react";
import { toast } from "sonner";

const INDUSTRIES = ["Sports", "Retail", "Finance", "Media", "Education", "SaaS", "Other"];

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/customers");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => `${c.company} ${c.main_contact}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb="Customers" title="Customers" subtitle="Companies in your workspace." actions={
        <Button onClick={() => setShowNew(true)} data-testid="new-customer-btn"><Plus className="w-4 h-4" strokeWidth={2}/>New customer</Button>
      } />
      <Card>
        <div className="px-5 py-3 flex items-center gap-3 border-b border-[hsl(var(--border))]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/>
            <Input placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-[280px]"/>
          </div>
          <div className="ml-auto text-[12px] text-[hsl(var(--text-muted))]">{filtered.length} of {items.length}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
          {filtered.map((c) => (
            <div key={c.id} className="group rounded-xl border border-[hsl(var(--border))] p-5 hover:border-[hsl(var(--primary))] hover:-translate-y-[1px] transition-all cursor-pointer bg-[hsl(var(--surface))]"
              onClick={() => nav(`/customers/${c.id}`)} data-testid="customer-card">
              <div className="flex items-center gap-3">
                <img src={c.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover ring-1 ring-[hsl(var(--border))]" onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }}/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{c.company}</div>
                  <div className="text-[12px] text-[hsl(var(--text-muted))] truncate">{c.industry}</div>
                </div>
                <StatusPill variant={c.status === "active" ? "success" : "default"}>{c.status}</StatusPill>
              </div>
              <div className="mt-4 grid grid-cols-3 text-center divide-x divide-[hsl(var(--border))]">
                <div>
                  <div className="font-mono font-semibold">£{(Math.round((c.mrr || 0) * 100) !== Math.round(c.mrr || 0) * 100) ? (c.mrr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (c.mrr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">MRR</div>
                </div>
                <div>
                  <div className="font-mono font-semibold">{(c.main_contact || "—").split(" ")[0]}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Contact</div>
                </div>
                <div>
                  <div className="font-mono font-semibold">{c.join_date ? new Date(c.join_date).getFullYear() : "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Since</div>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-full"><Empty title="No customers" subtitle="Click 'New customer' to create one."/></div>}
        </div>
      </Card>

      {showNew && <NewCustomerModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); load(); nav(`/customers/${c.id}`); }} />}
    </div>
  );
}

function NewCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    company: "", main_contact: "", email: "", phone: "",
    industry: "Other", status: "active", mrr: 0,
    logo_url: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.main_contact || !form.email) { toast.error("Company, main contact, email are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        mrr: parseFloat(form.mrr) || 0,
        logo_url: form.logo_url || "/brand/soubor-one-mark.png",
      };
      const { data } = await api.post("/customers", payload);
      toast.success("Customer added");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add customer");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="new-customer-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">New customer</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Company *</Label><Input required value={form.company} onChange={s("company")} data-testid="cust-company" placeholder="ACME Ltd"/></div>
          <div><Label>Main contact *</Label><Input required value={form.main_contact} onChange={s("main_contact")} placeholder="Jane Doe"/></div>
          <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={s("email")} placeholder="jane@acme.com"/></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={s("phone")} placeholder="+44 …"/></div>
          <div><Label>Industry</Label>
            <Select value={form.industry} onChange={s("industry")}>{INDUSTRIES.map((i) => <option key={i}>{i}</option>)}</Select>
          </div>
          <div><Label>Status</Label>
            <Select value={form.status} onChange={s("status")}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="churned">Churned</option>
            </Select>
          </div>
          <div><Label>MRR (£/mo)</Label><Input type="number" min="0" step="0.01" value={form.mrr} onChange={s("mrr")}/></div>
          <div className="md:col-span-2"><Label>Logo URL (optional)</Label><Input value={form.logo_url} onChange={s("logo_url")} placeholder="https://… (leave blank to auto-generate from initials)"/></div>
          <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={s("notes")} placeholder="Onboarding notes, key decision-makers, contract terms…"/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="cust-create-btn">{saving ? "Creating…" : "Create customer"}</Button>
        </div>
      </form>
    </div>
  );
}
